import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App.js';
import { ApiClient } from './lib/api-client.js';
import './styles.css';

/**
 * Admin Portal (React + Vite).
 *
 * The Gateway's origin is the only configuration the portal needs, and
 * `VITE_API_BASE_URL` remains the way to state it outright.
 *
 * The fallback keeps the **hostname the portal was served from** and changes
 * only the port. That is not a convenience: the Gateway resolves which tenant
 * a request belongs to from the Host header, so a portal served at
 * `acme.example.com` that called a hardcoded `example.com` would reach the
 * platform host instead, and every user of that tenant would be invisible to
 * login — reported as a bad credential, because an unknown address and a wrong
 * password are deliberately indistinguishable.
 *
 * Set `VITE_API_BASE_URL` when the Gateway is not on the same hostname; leave
 * it unset and tenant subdomains work without a build per tenant.
 */
const GATEWAY_PORT = '3000';

function defaultBaseUrl(): string {
  const { protocol, hostname } = window.location;
  // Local dev: the gateway runs on :3000. Production: the gateway is on the
  // same hostname over standard HTTPS (port 443).
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return `${protocol}//${hostname}:${GATEWAY_PORT}`;
  }
  return `${protocol}//${hostname}`;
}

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? defaultBaseUrl();

const container = document.getElementById('root');
if (container === null) throw new Error('Root container #root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App client={new ApiClient({ baseUrl })} />
  </StrictMode>,
);
